import { PropertySettings } from "@/types/dataset";
import { defineStore } from "pinia";
import { useActiveStore as active } from "./active";

export const useDisplayPaneStore = defineStore("displayPaneStore", {
	state: () => ({
		colorMaps: [
			{
				name: "Beachy",
				previewSrc: "beachy.png",
				colors: ["#18C59A", "#E4B13E", "#2881BE", "#AEEFC2", "#B3C5CE", "#2CC9B1"]
			},
			{
				name: "Gneiss to Meet You",
				previewSrc: "gneiss.png",
				colors: [
					"#FC9100",
					"#5D69B1",
					"#52BCA2",
					"#99C945",
					"#CB61B0",
					"#24796C",
					"#E2E670"
				]
			},
			{
				name: "Rainbow Categorical",
				previewSrc: "rainbow.png",
				colors: [
					"#FF0000",
					"#FF5C00",
					"#FFE500",
					"#ADFF00",
					"#05FF00",
					"#00F0FF",
					"#0075FF",
					"#2400FF",
					"#CC00FF"
				]
			},
			{
				name: "Take nothing for Granite",
				previewSrc: "take.png",
				colors: [
					"#9C0280",
					"#BCAEC6",
					"#4F8BF1",
					"#81C5BA",
					"#A46D61",
					"#DAB847",
					"#18C59A"
				]
			}
		],
		continuousColorMaps: [
			{
				name: "Action",
				previewSrc: "action.png",
				colors: ["#18C59A", "#E4B13E", "#2881BE", "#AEEFC2", "#B3C5CE", "#2CC9B1"]
			},
			{
				name: "Batlow",
				previewSrc: "batlow.png",
				colors: [
					"#FC9100",
					"#5D69B1",
					"#52BCA2",
					"#99C945",
					"#CB61B0",
					"#24796C",
					"#E2E670"
				]
			},
			{
				name: "Davos",
				previewSrc: "davos.png",
				colors: [
					"#FF0000",
					"#FF5C00",
					"#FFE500",
					"#ADFF00",
					"#05FF00",
					"#00F0FF",
					"#0075FF",
					"#2400FF",
					"#CC00FF"
				]
			},
			{
				name: "Fes",
				previewSrc: "fes.png",
				colors: [
					"#9C0280",
					"#BCAEC6",
					"#4F8BF1",
					"#81C5BA",
					"#A46D61",
					"#DAB847",
					"#18C59A"
				]
			},
			{
				name: "LaJolla",
				previewSrc: "lajolla.png",
				colors: [
					"#9C0280",
					"#BCAEC6",
					"#4F8BF1",
					"#81C5BA",
					"#A46D61",
					"#DAB847",
					"#18C59A"
				]
			},
			{
				name: "Libson",
				previewSrc: "libson.png",
				colors: [
					"#9C0280",
					"#BCAEC6",
					"#4F8BF1",
					"#81C5BA",
					"#A46D61",
					"#DAB847",
					"#18C59A"
				]
			},
			{
				name: "Rainbow Uniform",
				previewSrc: "rainbow-continuous.png",
				colors: [
					"#9C0280",
					"#BCAEC6",
					"#4F8BF1",
					"#81C5BA",
					"#A46D61",
					"#DAB847",
					"#18C59A"
				]
			},
			{
				name: "Roma",
				previewSrc: "roma.png",
				colors: [
					"#9C0280",
					"#BCAEC6",
					"#4F8BF1",
					"#81C5BA",
					"#A46D61",
					"#DAB847",
					"#18C59A"
				]
			}
		],
		displayStates: {} as any
	}),
	getters: {
		DISPLAY_STATES(): any {
			return this.displayStates;
		}
	},
	actions: {
		CLONE_ACTIVE_DISPLAY_STATE(viewId: string) {
			const activeDisplayState = JSON.parse(
				JSON.stringify(this.displayStates[active().view])
			);
			this.displayStates[viewId] = activeDisplayState;
		},
		SSA_SET_APPLIED_DEFAULT(
			layerName: string,
			hierarchyParentVals?:
				| {
						[key: string]: { [key: string]: string[] };
				  }
				| undefined
		) {
			const activeDatasetId: any = active().SSA_ACTIVE_DATASET.id;
			const activeView = active().view;

			const displayState = this.displayStates[activeView]?.[activeDatasetId];
			if (!displayState) return;

			const index = displayState.findIndex((item: any) => item.layerName === layerName);
			if (index === -1) return;

			const DS = displayState[index];
			DS.defaults_applied = true;
			if (hierarchyParentVals) {
				const hierarchyData = hierarchyParentVals[activeView]?.[layerName];
				if (hierarchyData) {
					// this is the hierarchy level initially selected - if the user selects the entire dataset, it should never be needed
					// Currently only the top level of the hierarchy is relevant - if deeper nested levels are applying defaults seperately, we need to increase specificity including all of hierarchy rather than hierarchyLevel[1]
					const defaultsAppliedToHierarchy: { [hierarchyLevel: string]: boolean } =
						DS.defaultsAppliedToHierarchy ?? {};
					for (const hierarchy of hierarchyData) {
						const hierarchyLevel = hierarchy.split("::");
						defaultsAppliedToHierarchy[hierarchyLevel[1]] = true;
					}
					DS.defaultsAppliedToHierarchy = defaultsAppliedToHierarchy;
				}
			}
		},
		SSA_SET_APPLIED_PROPERTY_DEFAULT(layerName: string, property: any) {
			const activeDatasetId: any = active().SSA_ACTIVE_DATASET.id;

			const DS =
				this.displayStates[active().view][activeDatasetId][
					this.displayStates[active().view][activeDatasetId].findIndex(
						(item: any) => item.layerName == layerName
					)
				];
			if (property) {
				if (!DS.propDefaultsApplied) DS.propDefaultsApplied = {};
				DS.propDefaultsApplied[property] = true;
			}
		},
		SSA_SET_PROPERTY_SETTING(layerName: string, property: string, settings: PropertySettings) {
			const activeDatasetId: any = active().SSA_ACTIVE_DATASET.id;

			const DS =
				this.displayStates[active().view][activeDatasetId][
					this.displayStates[active().view][activeDatasetId].findIndex(
						(item: any) => item.layerName == layerName
					)
				];
			if (property) {
				if (!DS.currentPropSettings) DS.currentPropSettings = {};
				if (!DS.currentPropSettings[property]) DS.currentPropSettings[property] = {};
				Object.assign(DS.currentPropSettings[property], settings);
			}
		},
		SET_DISPLAY_PANE_STATE(displayPaneState: any) {
			this.displayStates = {} as any;
			setTimeout(() => {
				this.displayStates = displayPaneState;
			}, 100);
		},
		ADD_DATASET_DISPLAY_STATE(displayState: any) {
			this.displayStates[active().view] = {
				...this.displayStates[active().view],
				...displayState
			};
		},
		SET_DISCRETE(val: boolean, layerIdentifier: string) {
			const activeDatasetId: any = active().SSA_ACTIVE_DATASET.id;
			const activeView = active().view;

			const displayState = this.displayStates[activeView]?.[activeDatasetId];
			if (!displayState) return;

			const layer = displayState.find(
				(item: any) => item.layerIdentifier === layerIdentifier
			);
			if (!layer?.configuration[0]) return;

			layer.configuration[0].isDiscrete = val;
		},
		SET_CATEGORY_COLOR(idx: number, color: string, layerIdentifier: string) {
			const activeDatasetId: any = active().SSA_ACTIVE_DATASET.id;
			const activeView = active().view;

			const displayState = this.displayStates[activeView]?.[activeDatasetId];
			if (!displayState) return;

			const layer = displayState.find(
				(item: any) => item.layerIdentifier === layerIdentifier
			);
			if (!layer?.configuration[0]?.categories) return;

			layer.configuration[0].categories[idx].color = color;
		},
		SET_USE_GAS_ATTRIBUTES(val: boolean, layerIdentifier: string) {
			const activeDatasetId: any = active().SSA_ACTIVE_DATASET.id;
			this.displayStates[active().view][activeDatasetId][
				this.displayStates[active().view][activeDatasetId].findIndex(
					(item: any) => item.layerIdentifier == layerIdentifier
				)
			].configuration[0].useGASAttributes = val;
		},
		SET_PREV_SELECTED_PROPERTY(prevSelectedProperty: any, layerName: string) {
			const activeDatasetId: any = active().SSA_ACTIVE_DATASET.id;

			this.displayStates[active().view][activeDatasetId][
				this.displayStates[active().view][activeDatasetId].findIndex(
					(item: any) => item.layerName == layerName
				)
			].configuration[0].previouslySelectedProperty = prevSelectedProperty;
		},
		SET_PROPERTY(
			property: {
				label: string;
				value: string;
				type: "continuous" | "categorical";
				isTimeStamp?: boolean;
			},
			layerIdentifier: string
		) {
			if (!active().SSA_ACTIVE_DATASET || !active().SSA_ACTIVE_DATASET.id) {
				console.log(`SET_PROPERTY ~ missing context`);
				return;
			}
			const activeDatasetId: any = active().SSA_ACTIVE_DATASET.id;

			if (activeDatasetId != null) {
				const displayState =
					this.displayStates[active().view][activeDatasetId][
						this.displayStates[active().view][activeDatasetId].findIndex(
							(item: any) => item.layerIdentifier == layerIdentifier
						)
					];
				if (displayState) {
					displayState.configuration[0].selectedProperty = property;

					// ssa().SSA_REPRESENTATION_UPDATE_PROPERTY({
					// 	layerName: active().SSA_ACTIVE_LAYER,
					// 	viewId: active().view,
					// 	type: "labels",
					// 	name: "CellFieldDataArrayName",
					// 	value: property.value
					// });
				}
			}
			return Promise.resolve();
		},
		SET_COLOR_MAP(colorMap: string, preset: any, layerIdentifier: string) {
			const activeDatasetId: any = active().SSA_ACTIVE_DATASET.id;
			const activeViewState = this.displayStates[active().view][activeDatasetId];
			const layerIndex = activeViewState.findIndex(
				(item: any) => item.layerIdentifier === layerIdentifier
			);

			if (layerIndex === -1) return;

			const layer = activeViewState[layerIndex];
			layer.configuration[0].colorMap = colorMap;
			layer.configuration[0].preset = preset;

			const CM = this.colorMaps.find((item: any) => item.name === colorMap);
			if (!CM) return;

			const categories = layer.configuration[0].categories;
			if (!categories) return;

			for (let i = 0; i < categories.length; i++) {
				categories[i].color = CM.colors[i] || "#aeaeae";
			}
		},
		SET_CONTINUOUS_COLOR_MAP(colorMap: string, preset: any, layerIdentifier: string) {
			if (active().SSA_ACTIVE_DATASET == null || active().SSA_ACTIVE_DATASET.id === undefined)
				return;
			const activeDatasetId: any = active().SSA_ACTIVE_DATASET.id;
			const displayStateIndex =
				this.displayStates[active().view][activeDatasetId] &&
				this.displayStates[active().view][activeDatasetId].findIndex(
					(item: any) => item.layerIdentifier == layerIdentifier
				);

			if (!displayStateIndex || displayStateIndex === -1) {
				return;
			}
			this.displayStates[active().view][activeDatasetId][
				this.displayStates[active().view][activeDatasetId].findIndex(
					(item: any) => item.layerIdentifier == layerIdentifier
				)
			].configuration[0].colorMap = colorMap;

			this.displayStates[active().view][activeDatasetId][
				this.displayStates[active().view][activeDatasetId].findIndex(
					(item: any) => item.layerIdentifier == layerIdentifier
				)
			].configuration[0].preset = preset;
		}
	},
	persist: false
});
